import {
  BISHOP,
  CASTLE_BK,
  CASTLE_BQ,
  CASTLE_WK,
  CASTLE_WQ,
  COLOR_MASK,
  type Color,
  FLAG_ACTION,
  FLAG_CAPTURE,
  KING,
  KNIGHT,
  PAWN,
  QUEEN,
  ROOK,
  TYPE_MASK,
  WHITE,
  colorBit,
  encodeMove,
  moveFrom,
  movePromo,
  moveTo,
  onBoard,
  other,
  pieceColor,
  squareName,
} from '../board.ts';
import { DIAGONAL, KNIGHT_JUMPS, ORTHOGONAL, type Vec, pieceDef } from '../pieces.ts';
import { FenError } from '../notation.ts';
import { type MoveContext, draw } from '../rules.ts';
import type { BotContext, Position, Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';
import { NOTE_CAUGHT, NOTE_CLEARED, NOTE_KING_SAVED } from './grey.ts';

// Day 17 (draft). Hidden armies, Stratego-style. First each player secretly arranges their
// pieces on their own two back rows. After that you see where the enemy pieces are but not
// what they are. Any piece may move like any piece (a bluff): a "pawn" can leap like a
// knight. On their turn, instead of moving, your opponent may challenge your last move: the
// piece is revealed, and if it couldn't move like that the move is taken back and you lose
// your turn; if it could, they lose theirs and you move again. Pieces are revealed when
// they're captured or challenged. There's no check: capture the king to win (taking a king
// with a bluff is always caught). Castling works if king and rook were set up on their
// usual squares. A real pawn reaching the far row becomes a queen. No en passant.
//
// Every move is an action. Payload: MOVE, CASTLE, SWAP (setup), READY (setup done), CHALLENGE.
//
// ext: [phase (0 White sets up, 1 Black, 2 play), swaps so far (repetition can't end a
// setup), last move: from + 1 (0: nothing to challenge), to, piece, captured, bluff, castling
// before it, whether the piece and its victim were revealed before it (bits 1, 2); revealed
// squares, low and high 32 bits; the 50-move count before the last move (not hashed: it grows
// every cycle and would hide repetitions); what the last challenge found (NOTE_*)]

export const MASK_MOVE = 0;
export const MASK_CASTLE = 1;
export const MASK_SWAP = 2;
export const MASK_READY = 3;
export const MASK_CHALLENGE = 4;

const PHASE = 0;
const SWAPS = 1;
const FROM = 2;
const TO = 3;
const PIECE = 4;
const CAPTURED = 5;
const BLUFF = 6;
const CASTLING = 7;
const WAS_SEEN = 8;
const SEEN_LO = 9;
const SEEN_HI = 10;
const HALFMOVE = 11;
const NOTE = 12;

export const PHASE_PLAY = 2;

/** Swaps allowed per setup (then only "ready"). */
export const MAX_SWAPS = 60;

export const MASQUERADE_FEN = `${STANDARD_FEN.replace('KQkq', '-')} w 0 -`;

const AMAZON: readonly Vec[] = [...ORTHOGONAL, ...DIAGONAL];

export function maskPhase(pos: { ext: Int32Array | null }): number {
  return pos.ext?.[PHASE] ?? PHASE_PLAY;
}

/** Whether the piece on `sq` has been revealed to both players. */
export function isSeen(pos: { ext: Int32Array | null }, sq: number): boolean {
  const ext = pos.ext;
  if (!ext) return true;
  return sq < 32 ? ((ext[SEEN_LO] >>> sq) & 1) === 1 : ((ext[SEEN_HI] >>> (sq - 32)) & 1) === 1;
}

function setSeen(ext: Int32Array, sq: number, on: boolean) {
  const i = sq < 32 ? SEEN_LO : SEEN_HI;
  const bit = 1 << (sq & 31);
  ext[i] = on ? ext[i] | bit : ext[i] & ~bit;
}

export interface MaskedLastMove {
  from: number;
  to: number;
  /** Whether the move was a bluff. Engine and referee only: the UI mustn't show it. */
  bluff: boolean;
}

export function maskChallengeable(pos: { ext: Int32Array | null }): MaskedLastMove | null {
  const ext = pos.ext;
  if (!ext || !ext[FROM]) return null;
  return { from: ext[FROM] - 1, to: ext[TO], bluff: ext[BLUFF] === 1 };
}

export function maskNote(pos: { ext: Int32Array | null }): number {
  return pos.ext?.[NOTE] ?? 0;
}

/** Squares a side arranges its army on. */
export function homeSquares(c: Color): number[] {
  const out: number[] = [];
  for (let sq = c === WHITE ? 0 : 48; sq < (c === WHITE ? 16 : 64); sq++) out.push(sq);
  return out;
}

/** Whether the real piece on `from` can make the move to `to` (the honest moves). */
export function honestMove(pos: Position, from: number, to: number): boolean {
  const b = pos.board;
  const p = b[from];
  const t = p & TYPE_MASK;
  const c = pieceColor(p);
  const df = (to & 7) - (from & 7);
  const dr = (to >> 3) - (from >> 3);
  if (t === PAWN) {
    const dir = c === WHITE ? 1 : -1;
    if (df === 0 && dr === dir) return b[to] === 0;
    if (df === 0 && dr === 2 * dir) return from >> 3 === (c === WHITE ? 1 : 6) && b[from + 8 * dir] === 0 && b[to] === 0;
    return Math.abs(df) === 1 && dr === dir && b[to] !== 0;
  }
  if (t === KNIGHT) return (Math.abs(df) === 1 && Math.abs(dr) === 2) || (Math.abs(df) === 2 && Math.abs(dr) === 1);
  if (t === KING) return Math.max(Math.abs(df), Math.abs(dr)) === 1;
  const straight = df === 0 || dr === 0;
  const diagonal = Math.abs(df) === Math.abs(dr);
  if ((t === ROOK && !straight) || (t === BISHOP && !diagonal) || (t === QUEEN && !straight && !diagonal)) return false;
  if (!straight && !diagonal) return false;
  const sf = Math.sign(df);
  const sr = Math.sign(dr);
  for (let sq = from + sf + 8 * sr; sq !== to; sq += sf + 8 * sr) if (b[sq]) return false;
  return true;
}

function pushTarget(b: Int8Array, from: number, to: number, them: number, out: number[], capturesOnly: boolean) {
  const q = b[to];
  if (q === 0) {
    if (!capturesOnly) out.push(encodeMove(from, to, MASK_MOVE, FLAG_ACTION));
  } else if (q & them) out.push(encodeMove(from, to, MASK_MOVE, FLAG_ACTION | FLAG_CAPTURE));
}

/** Every move any piece could make from `sq` (queen lines plus knight jumps): bluffs included. */
function anyMoves(pos: Position, sq: number, out: number[], capturesOnly: boolean) {
  const b = pos.board;
  const them = colorBit(other(pos.turn));
  for (const [df, dr] of AMAZON) {
    let f = (sq & 7) + df;
    let r = (sq >> 3) + dr;
    while (onBoard(f, r)) {
      const to = r * 8 + f;
      pushTarget(b, sq, to, them, out, capturesOnly);
      if (b[to]) break;
      f += df;
      r += dr;
    }
  }
  for (const [df, dr] of KNIGHT_JUMPS) {
    const f = (sq & 7) + df;
    const r = (sq >> 3) + dr;
    if (onBoard(f, r)) pushTarget(b, sq, r * 8 + f, them, out, capturesOnly);
  }
}

/** Only the moves the real piece on `sq` can make. */
function realMoves(pos: Position, sq: number, out: number[], capturesOnly: boolean) {
  const start = out.length;
  anyMoves(pos, sq, out, capturesOnly);
  let n = start;
  for (let i = start; i < out.length; i++) if (honestMove(pos, sq, moveTo(out[i]))) out[n++] = out[i];
  out.length = n;
}

function castles(pos: Position, out: number[]) {
  const us = pos.turn;
  const b = pos.board;
  const home = us === WHITE ? 4 : 60;
  const bit = colorBit(us);
  if (b[home] !== (KING | bit)) return;
  const rook = ROOK | bit;
  if (pos.castling & (us === WHITE ? CASTLE_WK : CASTLE_BK) && b[home + 1] === 0 && b[home + 2] === 0 && b[home + 3] === rook) {
    out.push(encodeMove(home, home + 2, MASK_CASTLE, FLAG_ACTION));
  }
  if (
    pos.castling & (us === WHITE ? CASTLE_WQ : CASTLE_BQ) &&
    b[home - 1] === 0 &&
    b[home - 2] === 0 &&
    b[home - 3] === 0 &&
    b[home - 4] === rook
  ) {
    out.push(encodeMove(home, home - 2, MASK_CASTLE, FLAG_ACTION));
  }
}

function setupMoves(pos: Position, out: number[]) {
  out.push(encodeMove(0, 0, MASK_READY, FLAG_ACTION));
  if (pos.ext![SWAPS] >= MAX_SWAPS) return;
  const home = homeSquares(pos.turn);
  for (const a of home) for (const b of home) if (a !== b && pos.board[a] !== pos.board[b]) out.push(encodeMove(a, b, MASK_SWAP, FLAG_ACTION));
}

function findKings(pos: Position) {
  pos.royals = [-1, -1];
  for (let sq = 0; sq < 64; sq++) {
    const p = pos.board[sq];
    if ((p & TYPE_MASK) === KING && p & COLOR_MASK) pos.royals[pieceColor(p) as Color] = sq;
  }
}

/** Castling is possible for a side whose king and rooks ended the setup on their usual squares. */
function castlingAfterSetup(b: Int8Array): number {
  let rights = 0;
  if (b[4] === (KING | colorBit(WHITE))) {
    if (b[7] === (ROOK | colorBit(WHITE))) rights |= CASTLE_WK;
    if (b[0] === (ROOK | colorBit(WHITE))) rights |= CASTLE_WQ;
  }
  if (b[60] === (KING | colorBit(1))) {
    if (b[63] === (ROOK | colorBit(1))) rights |= CASTLE_BK;
    if (b[56] === (ROOK | colorBit(1))) rights |= CASTLE_BQ;
  }
  return rights;
}

function clearLast(ext: Int32Array) {
  for (let i = FROM; i <= WAS_SEEN; i++) ext[i] = 0;
  ext[HALFMOVE] = 0;
}

function takeBack(ctx: MoveContext) {
  const { next } = ctx;
  const ext = next.ext!;
  const from = ext[FROM] - 1;
  const to = ext[TO];
  const piece = ext[PIECE];
  next.board[from] = piece;
  next.board[to] = ext[CAPTURED];
  next.castling = ext[CASTLING];
  next.halfmove = ext[HALFMOVE];
  findKings(next);
  // The bluffer's piece is unmasked by the challenge; its victim is back as it was.
  setSeen(ext, from, true);
  setSeen(ext, to, (ext[WAS_SEEN] & 2) !== 0);
  if (ctx.events) {
    ctx.events.push({ t: 'move', from: to, to: from, piece });
    if (ext[CAPTURED]) ctx.events.push({ t: 'add', sq: to, piece: ext[CAPTURED], cause: 'takeback' });
  }
  ctx.fx('caught', from);
}

function makeMasquerade(honest: boolean): Variant {
  return {
    id: honest ? 'masquerade-honest' : 'masquerade',
    info: {
      name: 'Masquerade Chess',
      but: 'every piece wears a mask',
      tagline: 'Set up your army in secret. Their pieces are masked, any piece can pretend to be any other, and bluffs can be called.',
      summary: 'Arrange your army in secret. Enemy pieces are **masked**, and any piece can move like any other, but a bluff can be **challenged**.',
      rules: [
        'First each player arranges their pieces on their own two back rows, in secret.',
        'You see where enemy pieces are, but not what they are. Pieces are revealed when captured or challenged.',
        'Any piece may move like any piece. Instead of moving, your opponent may challenge your last move: if your piece couldn’t move like that, it’s taken back and you lose your turn. If it could, they lose theirs.',
        'There’s no check: capture the king to win. Taking a king with a bluff is always caught.',
        'Castling works if your king and rook started on their usual squares. A real pawn on the far row becomes a queen. No en passant.',
      ],
      accent: '#ff5d8f',
      emoji: '🎭',
      bots: ['Open Book', 'Poker Face', 'Card Counter', 'Mentalist', 'Phantom'],
    },
    startFen: MASQUERADE_FEN,
    checks: false,
    castling: false,
    enPassant: false,
    extSize: 13,
    hiddenInfo: true,

    parseExtra(fields, pos) {
      const [phase = 'w', swaps = '0', last = '-', seen = '0,0'] = fields;
      const ext = pos.ext!;
      const ph = { w: 0, b: 1, p: 2 }[phase];
      if (ph === undefined) throw new FenError('Bad masquerade phase');
      ext[PHASE] = ph;
      ext[SWAPS] = Math.max(0, Number(swaps) | 0);
      if (last !== '-') {
        const v = last.split(',').map(Number);
        if (v.length !== 8 || v.some((n) => !Number.isInteger(n) || n < 0) || v[0] > 63 || v[1] > 63) throw new FenError('Bad last move');
        [ext[FROM], ext[TO], ext[PIECE], ext[CAPTURED], ext[BLUFF], ext[CASTLING], ext[HALFMOVE], ext[WAS_SEEN]] = [v[0] + 1, ...v.slice(1)];
      }
      const [lo = 0, hi = 0] = seen.split(',').map(Number);
      ext[SEEN_LO] = lo | 0;
      ext[SEEN_HI] = hi | 0;
    },

    formatExtra(pos) {
      const ext = pos.ext!;
      const last = ext[FROM]
        ? [ext[FROM] - 1, ext[TO], ext[PIECE], ext[CAPTURED], ext[BLUFF], ext[CASTLING], ext[HALFMOVE], ext[WAS_SEEN]].join(',')
        : '-';
      return ['wbp'[ext[PHASE]], String(ext[SWAPS]), last, `${ext[SEEN_LO]},${ext[SEEN_HI]}`];
    },

    hashExt: (pos) => pos.ext!.subarray(0, HALFMOVE),

    replaceMoves(pos, out, capturesOnly) {
      const ext = pos.ext!;
      if (ext[PHASE] < PHASE_PLAY) {
        if (!capturesOnly) setupMoves(pos, out);
        return true;
      }
      if (!honest && ext[FROM] && !capturesOnly) out.push(encodeMove(0, 0, MASK_CHALLENGE, FLAG_ACTION));
      const bit = colorBit(pos.turn);
      for (let sq = 0; sq < 64; sq++) {
        if (!(pos.board[sq] & bit)) continue;
        if (honest) realMoves(pos, sq, out, capturesOnly);
        else anyMoves(pos, sq, out, capturesOnly);
      }
      if (!capturesOnly) {
        const start = out.length;
        castles(pos, out);
        // A king going two squares sideways is the castle, not a bluff with the same squares.
        for (let i = start; i < out.length; i++) {
          const castle = out[i];
          const plain = out.findIndex((m) => movePromo(m) === MASK_MOVE && moveFrom(m) === moveFrom(castle) && moveTo(m) === moveTo(castle));
          if (plain >= 0) {
            out.splice(plain, 1);
            i--;
          }
        }
      }
      return true;
    },

    afterMove(ctx: MoveContext) {
      const { next, prev, us, them, move } = ctx;
      const ext = next.ext!;
      const kind = movePromo(move);
      ext[NOTE] = 0;
      if (kind === MASK_SWAP) {
        const a = moveFrom(move);
        const b = moveTo(move);
        const pa = next.board[a];
        next.board[a] = next.board[b];
        next.board[b] = pa;
        findKings(next);
        ext[SWAPS]++;
        next.halfmove = 0;
        next.turn = us;
        return;
      }
      if (kind === MASK_READY) {
        next.halfmove = 0;
        ext[SWAPS] = 0;
        ext[PHASE]++;
        if (ext[PHASE] === PHASE_PLAY) next.castling = castlingAfterSetup(next.board);
        return;
      }
      if (kind === MASK_CHALLENGE) {
        next.halfmove = prev.halfmove;
        if (ext[BLUFF]) {
          takeBack(ctx);
          ext[NOTE] = NOTE_CAUGHT;
          next.turn = us;
        } else {
          setSeen(ext, ext[TO], true);
          ctx.fx('cleared', ext[TO]);
          ext[NOTE] = NOTE_CLEARED;
          next.turn = them;
        }
        clearLast(ext);
        return;
      }
      const from = moveFrom(move);
      const to = moveTo(move);
      const piece = next.board[from];
      const captured = next.board[to];
      const castle = kind === MASK_CASTLE;
      ext[FROM] = castle ? 0 : from + 1;
      ext[TO] = to;
      ext[PIECE] = piece;
      ext[CAPTURED] = captured;
      ext[BLUFF] = castle || honestMove(prev, from, to) ? 0 : 1;
      ext[CASTLING] = prev.castling;
      ext[HALFMOVE] = prev.halfmove;
      ext[WAS_SEEN] = (isSeen(prev, from) ? 1 : 0) | (isSeen(prev, to) ? 2 : 0);
      ctx.relocate(from, to, 'capture');
      setSeen(ext, to, isSeen(prev, from));
      setSeen(ext, from, false);
      if ((piece & TYPE_MASK) === PAWN) {
        next.halfmove = 0;
        if (to >> 3 === (us === WHITE ? 7 : 0)) {
          next.board[to] = QUEEN | (piece & COLOR_MASK);
          // Hidden: no promotion event, the queen stays masked.
        }
      }
      if (castle) {
        const rf = to > from ? from + 3 : from - 4;
        const rt = to > from ? from + 1 : from - 1;
        ctx.relocate(rf, rt);
        // Castling shows both pieces for what they are.
        setSeen(ext, to, true);
        setSeen(ext, rt, true);
        setSeen(ext, rf, false);
      }
      if ((captured & TYPE_MASK) === KING && ext[BLUFF]) {
        takeBack(ctx);
        ext[NOTE] = NOTE_KING_SAVED;
        clearLast(ext);
      }
    },

    nextTurn(ctx) {
      return ctx.next.turn;
    },

    noMoves() {
      return draw('stalemate');
    },

    actionSan(pos, move) {
      const kind = movePromo(move);
      if (kind === MASK_SWAP) return 'swap';
      if (kind === MASK_READY) return 'ready';
      if (kind === MASK_CHALLENGE) return 'Challenge!';
      const from = moveFrom(move);
      const to = moveTo(move);
      if (kind === MASK_CASTLE) return to > from ? 'O-O' : 'O-O-O';
      return `${squareName(from)}${pos.board[to] ? 'x' : '–'}${squareName(to)}`;
    },

    botMove: honest ? undefined : (pos, bot) => masqueradeBot(pos, bot),
  };
}

export const masquerade: Variant = makeMasquerade(false);
/** No bluffs or challenges, for the computer's search. */
export const masqueradeHonest: Variant = makeMasquerade(true);

/** What a side has left that hasn't been revealed (counts are public: captures are revealed). */
function hiddenTypes(pos: Position, c: Color): { squares: number[]; types: number[] } {
  const bit = colorBit(c);
  const squares: number[] = [];
  const types: number[] = [];
  for (let sq = 0; sq < 64; sq++) {
    const p = pos.board[sq];
    if (!(p & bit) || isSeen(pos, sq)) continue;
    squares.push(sq);
    types.push(p & TYPE_MASK);
  }
  return { squares, types };
}

/** Types able to make a move of this shape (ignoring blockers). */
function shapeTypes(from: number, to: number, c: Color): number[] {
  const df = Math.abs((to & 7) - (from & 7));
  const drSigned = (to >> 3) - (from >> 3);
  const dr = Math.abs(drSigned);
  const forward = c === WHITE ? drSigned > 0 : drSigned < 0;
  const out: number[] = [];
  if ((df === 1 && dr === 2) || (df === 2 && dr === 1)) return [KNIGHT];
  if (Math.max(df, dr) === 1) out.push(KING);
  if (forward && ((df === 0 && dr <= 2) || (df === 1 && dr === 1))) out.push(PAWN);
  if (df === 0 || dr === 0) out.push(ROOK, QUEEN);
  else if (df === dr) out.push(BISHOP, QUEEN);
  return out;
}

const SUSPICION = [0.15, 0.35, 0.55, 0.75, 0.9];

function masqueradeBot(pos: Position, bot: BotContext): number | null {
  const ext = pos.ext!;
  const us = pos.turn;
  const them = other(us);
  const level = Math.max(0, Math.min(4, bot.level - 1));
  if (ext[PHASE] < PHASE_PLAY) {
    // A few secret swaps along the back row, then ready.
    if (ext[SWAPS] < 4 && bot.random() < 0.7) {
      const row = us === WHITE ? 0 : 56;
      const a = row + Math.floor(bot.random() * 8);
      let b = row + Math.floor(bot.random() * 8);
      if (a === b) b = row + ((b - row + 1) & 7);
      if (pos.board[a] !== pos.board[b]) return encodeMove(a, b, MASK_SWAP, FLAG_ACTION);
    }
    return encodeMove(0, 0, MASK_READY, FLAG_ACTION);
  }

  // Challenge using only what a player could know: what the moved piece has shown, and how
  // many of their hidden pieces could move like that.
  const last = maskChallengeable(pos);
  if (last) {
    const piece = pos.board[last.to];
    let pHonest: number;
    const shapes = shapeTypes(last.from, last.to, them);
    if (isSeen(pos, last.to)) pHonest = shapes.includes(piece & TYPE_MASK) ? 1 : 0;
    else {
      const { types } = hiddenTypes(pos, them);
      // The moved piece is hidden, so it's one of these (counting it where it now stands).
      const able = types.filter((t) => shapes.includes(t)).length;
      pHonest = types.length ? able / types.length : 1;
    }
    const stakes = ext[CAPTURED] ? 1.4 : 1;
    if (bot.random() < (1 - pHonest) * SUSPICION[level] * stakes) return encodeMove(0, 0, MASK_CHALLENGE, FLAG_ACTION);
  }

  // Search a guess: the enemy's hidden pieces shuffled among their hidden squares.
  const view: Position = { ...pos, board: pos.board.slice(), royals: [pos.royals[0], pos.royals[1]], ext: pos.ext!.slice() };
  const { squares, types } = hiddenTypes(pos, them);
  for (let i = types.length - 1; i > 0; i--) {
    const j = Math.floor(bot.random() * (i + 1));
    [types[i], types[j]] = [types[j], types[i]];
  }
  squares.forEach((sq, i) => (view.board[sq] = types[i] | colorBit(them)));
  findKings(view);
  // No last move to challenge in the guess (it would need the real pieces).
  clearLast(view.ext!);
  return bot.search(masqueradeHonest, view);
}
